import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createClient } from '@supabase/supabase-js';
import Web3 from 'web3'; 

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const useWalletStore = create(
  persist(
    (set, get) => ({
      wallet: null,
      isConnecting: false,
      userData: null,
      
      setWallet: (walletInfo) => set({ wallet: walletInfo }),
      setIsConnecting: (status) => set({ isConnecting: status }),
      setUserData: (data) => set({ userData: data }),
      
      createOrUpdateUser: async (walletAddress) => {
        try {
          // Check if user exists
          const { data: existingUser, error: fetchError } = await supabase
            .from('members')
            .select('id, wallet_address, points')
            .eq('wallet_address', walletAddress)
            .single();

          if (fetchError && fetchError.code !== 'PGRST116') {
            throw fetchError;
          }

          if (existingUser) {
            set({ userData: existingUser });
            return { success: true, user: existingUser };
          }

          // Create new user if doesn't exist
          const { data: newUser, error: insertError } = await supabase
            .from('members')
            .insert([ 
              { 
                wallet_address: walletAddress,
                points: 0
              }
            ])
            .select()
            .single();

          if (insertError) throw insertError;

          set({ userData: newUser });
          return { success: true, user: newUser };
        } catch (error) {
          console.error('Error managing user:', error);
          return { success: false, error: error.message };
        }
      },

      connectWallet: async () => {
        const { wallet } = get();
        
        if (typeof window.ethereum === 'undefined') {
          return { success: false, error: 'MetaMask wallet not found' };
        }

        try {
          set({ isConnecting: true });
          
          const web3 = new Web3(window.ethereum);
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: '0x27B7',
              chainName: 'Monad Testnet',
              rpcUrls: ['https://quick-long-glade.monad-testnet.quiknode.pro/e295db338d6d5de04640a9d08676b1c815cbe975/'],
              nativeCurrency: {
                name: 'MON',
                symbol: 'MON',
                decimals: 18,
              },
              blockExplorerUrls: ['https://testnet.monadexplorer.com'],
            }],
          });

          const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
          const publicKey = accounts[0];

          const newWalletInfo = {
            address: publicKey,
            type: 'Ethereum',
            shortAddress: publicKey.slice(0, 4) + '...' + publicKey.slice(-4)
          };

          const { success: userSuccess, error: userError } = await get().createOrUpdateUser(publicKey);
          
          if (!userSuccess) {
            console.error('Failed to create/update user:', userError);
          }
          
          set({ wallet: newWalletInfo });
          return { success: true, wallet: newWalletInfo };
        } catch (error) {
          console.error('Error connecting to wallet:', error);
          return { success: false, error: error.message };
        } finally {
          set({ isConnecting: false });
        }
      },

      disconnectWallet: async () => {
        try {
          // MetaMask does not have a disconnect method, so we just clear the wallet info
          set({ wallet: null, userData: null });
          return { success: true };
        } catch (error) {
          console.error('Error disconnecting wallet:', error);
          return { success: false, error: error.message };
        }
      },
    }),
    {
      name: 'wallet-storage',
      getStorage: () => localStorage,
    }
  )
);

export default useWalletStore;